import { RestrictedScreen } from 'component/admin';
import { Pad, PadBox } from 'component/basics';
import { Heading, WebLinkTextButton } from 'component/text';
import { useLanguage } from 'component/translation';
import { EMAIL_PREVIEW_CAPABILITY } from 'feature/feature-roles';
import type { AdminConfig } from 'structure/admin';
import { makeApiUrl } from 'system/servercall';
import { useSiloKey } from 'util/datastore';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export const EmailPreviewFeature: Feature<AdminConfig, {}> = {
    name: 'Email Preview',
    key: 'emailpreview',
    subscreens: {
        emailPreview: EmailPreviewScreen,
    },
    providesConfig: {
        quickLinks: [{ label: t('!Email Preview'), screenKey: 'emailPreview', capability: EMAIL_PREVIEW_CAPABILITY }],
    },
};

const previewEmails = [
    {
        name: 'Reply Notif',
        templateId: 'reply-notif',
        data: {
            replyAuthorName: 'John Doe',
            conversationName: 'Test Conversation',
            replyText: 'This is a test reply',
            notifUrl: 'https://www.google.com',
        },
    },
    {
        name: 'Mod Approve',
        templateId: 'mod-approve',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            commentText: 'This is a test comment',
        },
    },
    {
        name: 'Mod Reject',
        templateId: 'mod-reject',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            reasoningTextLines: [
                'This would be okay if you removed the profanity',
                'And if you avoided calling for the death of your opponents.',
            ],
            commentText: 'This is a test comment',
        },
    },
    {
        name: 'Mod Reject (New Moderation Dashboard)',
        templateId: 'mod-reject',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            reasoningArray: ['Express your ideas with civility', 'Explain disagreements constructively'],
            customMessage: 'Voicing your disagreement is fine, but please do so in a respectful manner.',
            commentText: 'This is a test comment',
        },
    },
    {
        name: 'Mod Warn',
        templateId: 'mod-warn',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            reasoningArray: ['Express your ideas with civility', 'Explain disagreements constructively'],
            commentText: 'This is a test comment',
            messageFromModerator: 'This is a moderator message',
            NUM_WARNINGS: '3',
            LINK: 'https://google.com',
            customMessage: 'Voicing your disagreement is fine, but please do so in a respectful manner.',
            COMMUNITY_GUIDELINES_URL: 'https://psi.zdf.de/pages/np/community-guidelines.html',
        },
    },
    {
        name: 'Mod Reject Block',
        templateId: 'mod-reject-block',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            reasoningArray: ['Express your ideas with civility', 'Explain disagreements constructively'],
            commentText: 'This is a test comment',
            messageFromModerator: 'This is a moderator message',
            LINK: 'https://google.com',
            customMessage: 'Voicing your disagreement is fine, but please do so in a respectful manner.',
            COMMUNITY_GUIDELINES_URL: 'https://psi.zdf.de/pages/np/community-guidelines.html',
        },
    },
    {
        name: 'Mod Block',
        templateId: 'mod-block',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            LINK: 'https://google.com',
        },
    },
    {
        name: 'Mod Unblock',
        templateId: 'mod-unblock',
        data: {
            moderatorName: 'John Doe',
            conversationName: 'Test Conversation',
            commentText: 'This is a test comment',
            LINK: 'https://google.com',
            COMMUNITY_GUIDELINES_URL: 'https://psi.zdf.de/pages/np/community-guidelines.html',
        },
    },
];

export function makeEmailPreviewUrl(preview: { templateId: string; data: any }, siloKey: string, language: string | null) {
    const url = new URL(makeApiUrl('debug', 'renderEmailTemplate'));
    url.searchParams.set('templateId', preview.templateId);
    url.searchParams.set('data', JSON.stringify(preview.data));
    url.searchParams.set('siloKey', siloKey);
    url.searchParams.set('language', language || 'english');
    return url.toString();
}

export function EmailPreviewScreen() {
    const siloKey = useSiloKey();
    const language = useLanguage();

    return (
        <RestrictedScreen capability={EMAIL_PREVIEW_CAPABILITY} pad>
            <PadBox bottom={20} horiz={20}>
                <Pad />
                <Heading label={t('!Email Preview')} type='large' weight='medium' />
                <Pad />
                {previewEmails.map((preview, index) => (
                    <WebLinkTextButton
                        key={preview.templateId + index}
                        text={preview.name}
                        url={makeEmailPreviewUrl(preview, siloKey, language)}
                    />
                ))}
            </PadBox>
        </RestrictedScreen>
    );
}
